// src/components/ShiftManager.js
import React, { useState, useEffect } from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import * as dateUtils from '../utils/dateUtils';

function ShiftManager({ userInfo }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [shifts, setShifts] = useState([]);
  const [defaultTimes, setDefaultTimes] = useState({
    startTime: '09:00',
    endTime: '18:00'
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchShifts = async () => {
      try {
        const token = localStorage.getItem('token');
        console.log('Raw token:', token);

        if (!token) {
          throw new Error('認証トークンがありません');
        }

        const response = await fetch(`/api/shifts/${selectedMonth}`, {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Server error:', errorData);
          throw new Error(errorData.message || 'シフトデータの取得に失敗しました');
        }

        const data = await response.json();
        console.log('Received shifts data:', data);
        
        const shiftsMap = data.reduce((acc, shift) => {
          // 日付の処理 - APIから取得した日付（UTC）を日本時間（JST）に変換
          // PostgreSQLのタイムゾーンはAsia/Tokyoに設定されているため、
          // 日付を正しく表示するには、UTCから日本時間に変換する必要がある
          
          // 統一されたDB日付変換関数を使用
          const dateStr = dateUtils.formatDBDateToJST(shift.date);
          console.log('Original date from API:', shift.date);
          console.log('Converted date to JST:', dateStr);

          acc[dateStr] = {
            date: dateStr,
            startTime: shift.start_time ? shift.start_time.slice(0, 5) : '',
            endTime: shift.end_time ? shift.end_time.slice(0, 5) : '',
            isWorkday: !!(shift.start_time && shift.end_time)
          };
          return acc;
        }, {});

        // dateUtils.jsを使用して月の日付を生成
        const datesForMonth = dateUtils.generateDatesForMonth(selectedMonth);
        const formattedShifts = [];

        // 各日付に対してシフト情報を設定
        datesForMonth.forEach(dateStr => {
          formattedShifts.push(shiftsMap[dateStr] || {
            date: dateStr,
            startTime: '',
            endTime: '',
            isWorkday: false
          });
        });

        setShifts(formattedShifts);
      } catch (error) {
        console.error('シフトデータの取得エラー:', error);
        setMessage(error.message);
      }
    };

    fetchShifts();
  }, [selectedMonth, userInfo.employeeId]);

  const handleSave = async () => {
    try {
      // シフトデータを送信する前に、日付を処理
      // 日本時間の日付をそのまま送信（サーバー側でUTCに変換される）
      console.log('Original shifts:', shifts);
      
      const shiftsToSave = shifts.map(shift => {
        return {
          ...shift,
          // 日付はそのまま送信（YYYY-MM-DD形式）
          date: shift.date
        };
      });
      
      console.log('Shifts to save:', shiftsToSave);
      
      const response = await fetch('/api/shifts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token')
        },
        body: JSON.stringify({
          month: selectedMonth,
          shifts: shiftsToSave
        })
      });

      if (!response.ok) {
        throw new Error('保存に失敗しました');
      }

      setMessage('シフトを保存しました');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('シフト保存エラー:', error);
      setMessage('保存に失敗しました');
    }
  };

  const handleShiftChange = (index, field, value) => {
    const newShifts = [...shifts];
    if (field === 'isWorkday') {
      newShifts[index] = {
        ...newShifts[index],
        isWorkday: value,
        startTime: value ? defaultTimes.startTime : '',
        endTime: value ? defaultTimes.endTime : ''
      };
    } else {
      newShifts[index] = {
        ...newShifts[index],
        [field]: value
      };
    }
    setShifts(newShifts);
  };

  const calculateWorkHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    const workMinutes = totalMinutes > 60 ? totalMinutes - 60 : totalMinutes;
    return Math.round(workMinutes / 60 * 100) / 100;
  };

  const monthlyStats = shifts.reduce((acc, shift) => {
    if (shift.isWorkday) {
      acc.workdays++;
      acc.totalHours += calculateWorkHours(shift.startTime, shift.endTime);
    }
    return acc;
  }, { workdays: 0, totalHours: 0 });

  const DesktopView = () => (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">シフト管理</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span>基本勤務時間：</span>
              <input
                type="time"
                value={defaultTimes.startTime}
                onChange={(e) => setDefaultTimes({...defaultTimes, startTime: e.target.value})}
                className="border rounded px-2 py-1"
              />
              <span>〜</span>
              <input
                type="time"
                value={defaultTimes.endTime}
                onChange={(e) => setDefaultTimes({...defaultTimes, endTime: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="border rounded px-3 py-2"
            />
          </div>
        </div>

        {message && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
            {message}
          </div>
        )}

        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-4 py-2 w-32">日付</th>
              <th className="border px-4 py-2">勤務</th>
              <th className="border px-4 py-2">開始時間</th>
              <th className="border px-4 py-2">終了時間</th>
              <th className="border px-4 py-2">休憩時間</th>
            </tr>
          </thead>
          <tbody>
            {shifts.map((shift, index) => {
              const date = new Date(shift.date);
              const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
              const day = date.getDate();
              const isWeekend = dayOfWeek === '土' || dayOfWeek === '日';

              return (
                <tr key={index} className={isWeekend ? 'bg-gray-50' : ''}>
                  <td className="border px-4 py-2">
                    {`${Number(day)}日 (${dayOfWeek})`}
                  </td>
                  <td className="border px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={shift.isWorkday}
                      onChange={(e) => handleShiftChange(index, 'isWorkday', e.target.checked)}
                    />
                  </td>
                  <td className="border px-4 py-2">
                    <input
                      type="time"
                      value={shift.startTime}
                      onChange={(e) => handleShiftChange(index, 'startTime', e.target.value)}
                      disabled={!shift.isWorkday}
                      className="w-full px-2 py-1 border rounded"
                    />
                  </td>
                  <td className="border px-4 py-2">
                    <input
                      type="time"
                      value={shift.endTime}
                      onChange={(e) => handleShiftChange(index, 'endTime', e.target.value)}
                      disabled={!shift.isWorkday}
                      className="w-full px-2 py-1 border rounded"
                    />
                  </td>
                  <td className="border px-4 py-2 text-center">
                    {shift.isWorkday ? '1:00' : '---'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-6 flex justify-between items-center">
          <div className="text-lg">
            <div>予定勤務日数: {monthlyStats.workdays}日</div>
            <div>予定勤務時間: {monthlyStats.totalHours.toFixed(1)}時間</div>
          </div>
          <button
            onClick={handleSave}
            className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );

  const MobileView = () => (
    <div className="p-2">
      <div className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex flex-col space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">シフト管理</h2>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-2 py-1 border rounded text-sm"
            />
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-bold mb-2">基本勤務時間</h3>
            <div className="flex gap-2 text-sm">
              <div className="flex-1">
                <input
                  type="time"
                  value={defaultTimes.startTime}
                  onChange={(e) => setDefaultTimes({...defaultTimes, startTime: e.target.value})}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
              <span className="text-gray-500">〜</span>
              <div className="flex-1">
                <input
                  type="time"
                  value={defaultTimes.endTime}
                  onChange={(e) => setDefaultTimes({...defaultTimes, endTime: e.target.value})}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
            </div>
          </div>
        </div>

        {message && (
          <div className="mt-4 p-3 bg-green-100 text-green-700 rounded text-sm">
            {message}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {shifts.map((shift, index) => {
          const date = new Date(shift.date);
          const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
          const day = date.getDate();
          const isWeekend = dayOfWeek === '土' || dayOfWeek === '日';

          return (
            <div 
              key={index}
              className={`bg-white rounded-lg shadow-md p-3 ${isWeekend ? 'bg-gray-50' : ''}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium">
                  {`${Number(day)}日 (${dayOfWeek})`}
                </span>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={shift.isWorkday}
                    onChange={(e) => handleShiftChange(index, 'isWorkday', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm">勤務</span>
                </label>
              </div>

              {shift.isWorkday && (
                <div className="flex gap-2 text-sm">
                  <input
                    type="time"
                    value={shift.startTime}
                    onChange={(e) => handleShiftChange(index, 'startTime', e.target.value)}
                    className="flex-1 px-2 py-1 border rounded"
                  />
                  <span className="text-gray-500">〜</span>
                  <input
                    type="time"
                    value={shift.endTime}
                    onChange={(e) => handleShiftChange(index, 'endTime', e.target.value)}
                    className="flex-1 px-2 py-1 border rounded"
                  />
                </div>
              )}
              {shift.isWorkday && (
                <div className="mt-2 text-sm text-gray-600">
                  <span>休憩時間: 1:00</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow-md p-3 mt-4">
        <div className="text-sm">
          <div>予定勤務日数: {monthlyStats.workdays}日</div>
          <div>予定勤務時間: {monthlyStats.totalHours.toFixed(1)}時間</div>
        </div>
      </div>

      <button
        onClick={handleSave}
        className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg shadow mt-4 text-sm"
      >
        保存する
      </button>
    </div>
  );

  return isDesktop ? <DesktopView /> : <MobileView />;
}

export default ShiftManager;
